import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Salad, LineChart, Utensils, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1490645935967-10de6ba17061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbWVhbCUyMHRvcCUyMHZpZXd8ZW58MHx8fHwxNzg2NjIwODE2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-sand grid lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img
          src={HERO}
          alt="Healthy meal from above"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-deep-olive/45" />
        <div className="relative h-full flex flex-col justify-between p-12 text-sand">
          <span className="text-xs font-bold uppercase tracking-[0.28em] text-ochre">
            MBG Meal Tracker
          </span>
          <div className="max-w-md">
            <h2 className="font-heading text-3xl font-semibold leading-snug">
              Every plate, before and after.
            </h2>
            <p className="mt-4 text-sm text-sand/80 leading-relaxed">
              The same log your phone writes to, now on the big screen — with the
              charts that never fit in your pocket.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center px-6 sm:px-12 lg:px-16 py-16">
        <div className="w-full max-w-md animate-rise-in">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-forest">
              <Utensils className="h-5 w-5 text-sand" strokeWidth={2.2} />
            </span>
            <span className="font-heading text-lg font-semibold text-deep-olive tracking-tight">
              MBG Tracker
            </span>
          </div>

          <h1 className="mt-12 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-deep-olive">
            Your meal log,
            <br />
            on the web.
          </h1>
          <p className="mt-5 text-base text-stone-grey leading-relaxed">
            Sign in with the same Google account you use on mobile. Your feed,
            intake and waste stats sync from one shared database.
          </p>

          <Button
            data-testid="google-signin-button"
            onClick={signIn}
            className="mt-10 h-12 rounded-full bg-forest px-7 text-sand hover:bg-forest-hover transition-colors duration-200 group"
          >
            Continue with Google
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Button>

          <div className="mt-14 space-y-4 border-t border-[#E8E3D9] pt-8">
            {[
              { icon: Salad, t: "Shared meal feed", d: "Everything logged on mobile shows up here instantly." },
              { icon: LineChart, t: "Intake & trends", d: "Daily macros plus 7 and 30 day charts." },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={t} className="flex gap-4 animate-rise-in" style={{ animationDelay: `${120 + i * 90}ms` }}>
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-moss" strokeWidth={2} />
                <div>
                  <p className="font-heading font-medium text-deep-olive">{t}</p>
                  <p className="text-sm text-stone-grey">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
